import { onValue, ref, remove, update } from 'firebase/database';
import React, { useEffect, useState } from 'react';
import { LuMessageSquare as FiMessageSquare, LuEyeOff as FiEyeOff, LuSettings as FiSettings } from "react-icons/lu";
import { database } from '../constants/firebase';
import { useUser } from '../contexts/UserContext';

const BlockUserBottomSheet = ({ user, onClose, setUpdateUi }) => {
    const { currentUser } = useUser();
    const [isClosing, setIsClosing] = useState(false);
    const [isBlocking, setIsBlocking] = useState(false);
    const [isBlockedByMe, setIsBlockedByMe] = useState(false);
    const [isBlockedByOther, setIsBlockedByOther] = useState(false);

    useEffect(() => {
        if (!currentUser?.uid || !user?.uid) return;

        const myBlockRef = ref(database, `Users/${currentUser.uid}/blockedUsers/${user.uid}`);
        const theirBlockRef = ref(database, `Users/${user.uid}/blockedUsers/${currentUser.uid}`);

        // ✅ Keep realtime sync
        const unsubMy = onValue(myBlockRef, (snapshot) => {
            setIsBlockedByMe(snapshot.exists());
        });

        const unsubTheir = onValue(theirBlockRef, (snapshot) => {
            setIsBlockedByOther(snapshot.exists());
        });

        return () => {
            unsubMy();
            unsubTheir();
        };
    }, [currentUser?.uid, user?.uid]);

    const handleToggleBlock = async () => {
        if (!currentUser?.uid || !user?.uid) return;
        setIsBlocking(true);

        const blockRef = ref(database, `Users/${currentUser.uid}/blockedUsers/${user.uid}`);

        if (isBlockedByMe) {
            // ✅ Unblock
            await remove(blockRef);
        } else {
            // ✅ Block
            await update(ref(database, `Users/${currentUser.uid}/blockedUsers`), {
                [user.uid]: true,
            });
        }

        // ✅ Don’t force state here — listeners will update isBlockedByMe

        setIsClosing(true);
        setTimeout(() => {
            setIsClosing(false);
            onClose();
        }, 300);
        setUpdateUi((prev) => !prev);
        setIsBlocking(false);
    };

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setIsClosing(false);
            onClose();
        }, 300);
    };

    if (!user) return null;

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-end z-50"
            onClick={handleClose}
        >
            <div
                className={`bg-white dark:bg-[#1E1E1E] w-full max-w-2xl rounded-t-3xl p-6 text-center ${isClosing ? 'animate-slide-down' : 'animate-slide-up'
                    }`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* User Avatar */}
                <img
                    src={
                        user.profilePic ||
                        'https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/avatar-1.webp?alt=media&token=2d65470d-2104-4787-a5ef-cbfceb8a63ef'
                    }
                    alt={user.name}
                    className="w-24 h-24 rounded-full object-cover mx-auto mb-4"
                />

                {/* Title */}
                <h2 className="text-2xl font-bold text-black dark:text-white">
                    {isBlockedByOther && !isBlockedByMe
                        ? `${user.name} has blocked you`
                        : isBlockedByMe
                            ? `Unblock ${user.name}`
                            : `Block ${user.name}`}
                </h2>

                {/* Subtitle */}
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    {isBlockedByOther && !isBlockedByMe
                        ? 'You cannot block or message this user.'
                        : 'This will also block any other accounts they may have or create in the future.'}
                </p>

                {/* Info List */}
                {!isBlockedByOther && (
                    <ul className="text-left space-y-4 mt-6">
                        <li className="flex items-center gap-4">
                            <FiMessageSquare className="w-6 h-6 text-gray-600 dark:text-gray-300" />
                            <span className="text-gray-800 dark:text-gray-200">
                                They won't be able to message you or find your profile or content on Zylen
                            </span>
                        </li>
                        <li className="flex items-center gap-4">
                            <FiEyeOff className="w-6 h-6 text-gray-600 dark:text-gray-300" />
                            <span className="text-gray-800 dark:text-gray-200">
                                They won't be notified that you blocked them.
                            </span>
                        </li>
                        <li className="flex items-center gap-4">
                            <FiSettings className="w-6 h-6 text-gray-600 dark:text-gray-300" />
                            <span className="text-gray-800 dark:text-gray-200">
                                You can unblock them anytime in Chat Settings.
                            </span>
                        </li>
                    </ul>
                )}

                {/* Block Button */}
                {!isBlockedByOther && (
                    <button
                        onClick={handleToggleBlock}
                        disabled={isBlocking}
                        className="w-full bg-primary text-white font-bold py-4 rounded-full mt-8 hover:bg-primary-dark transition disabled:opacity-50"
                    >
                        {isBlocking
                            ? 'Please Wait...'
                            : isBlockedByMe
                                ? 'Unblock'
                                : 'Block'}
                    </button>
                )}
            </div>
        </div>
    );
};

export default BlockUserBottomSheet;
